import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChevronDown, LogOut, Save, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, resolveImage } from "@/lib/api";

const isValidImageSource = (value = "") => {
  const text = value.toString().trim();
  if (!text) return false;
  if (text.includes("@") && !text.startsWith("http")) return false;
  return (
    text.startsWith("/api/uploads/") ||
    text.startsWith("/uploads/") ||
    text.startsWith("/images/") ||
    text.startsWith("http://") ||
    text.startsWith("https://") ||
    text.startsWith("data:image")
  );
};

const cleanPhotoValue = (value = "") => (isValidImageSource(value) ? value.toString().trim() : "");

const UserProfileMenu = ({ align = "right" }) => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", profile_photo: "", password: "" });

  useEffect(() => {
    setForm({
      name: user?.name || "",
      profile_photo: cleanPhotoValue(user?.profile_photo || ""),
      password: "",
    });
  }, [user]);

  const avatar = cleanPhotoValue(form.profile_photo || user?.profile_photo || "");
  const initial = (user?.name || user?.email || "U").trim().slice(0, 1).toUpperCase();

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploadedUrl = cleanPhotoValue(data.url || data.path || "");
      setForm((current) => ({ ...current, profile_photo: uploadedUrl }));
      toast.success("Foto enviada. Clique em Salvar perfil.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao enviar foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        profile_photo: cleanPhotoValue(form.profile_photo),
      };
      if (form.password) payload.password = form.password;

      const { data } = await api.patch("/auth/profile", payload);
      updateUser(data);
      setForm((current) => ({ ...current, profile_photo: cleanPhotoValue(data.profile_photo || current.profile_photo), password: "" }));
      toast.success("Perfil atualizado!");
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao salvar perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-white hover:bg-secondary/40 text-sm font-medium"
      >
        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground overflow-hidden flex items-center justify-center text-xs font-semibold">
          {avatar ? <img src={resolveImage(avatar)} alt="Perfil" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : initial}
        </div>
        <span className="hidden md:inline max-w-[160px] truncate">{user?.name || user?.email}</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {open && (
        <div className={`absolute ${align === "left" ? "left-0" : "right-0"} mt-3 w-[330px] bg-white border border-border rounded-2xl shadow-xl p-5 z-50`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground overflow-hidden flex items-center justify-center font-semibold">
              {avatar ? <img src={resolveImage(avatar)} alt="Perfil" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <User className="w-6 h-6" />}
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg text-primary truncate">{user?.name || "Usuário"}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Nome</label>
              <input
                value={form.name || ""}
                onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Foto do perfil</label>
              <div className="flex gap-2 mt-1">
                <input
                  value={cleanPhotoValue(form.profile_photo)}
                  onChange={(e) => setForm((current) => ({ ...current, profile_photo: e.target.value }))}
                  placeholder="URL da imagem ou clique em Trocar foto"
                  className="flex-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary"
                />
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
              >
                <Camera className="w-4 h-4" /> {uploading ? "Enviando..." : "Trocar foto"}
              </button>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Nova senha</label>
              <input
                type="password"
                value={form.password || ""}
                onChange={(e) => setForm((current) => ({ ...current, password: e.target.value }))}
                placeholder="Deixe vazio para manter a atual"
                className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary"
              />
            </div>

            <button type="button" onClick={saveProfile} disabled={saving || uploading} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-accent text-accent-foreground text-sm font-medium disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar perfil"}
            </button>

            <button type="button" onClick={handleLogout} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-border text-destructive hover:bg-red-50 text-sm font-medium">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileMenu;
